import { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { apiFetch, ApiError } from '@/lib/api';
import { describeApiError, ApiErrorDescription } from '@/lib/apiError';

export default function AppEntry() {
  const [params] = useSearchParams();
  const next = params.get('next') || 'dashboard';
  const [target, setTarget] = useState<string | null>(null);
  const [failure, setFailure] = useState<ApiErrorDescription | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setFailure(null);
    apiFetch<{ workspaces: { id: string }[] }>('/workspaces')
      .then(({ workspaces }) => {
        if (!active) return;
        if (workspaces && workspaces.length > 0) {
          localStorage.setItem('workspaceId', workspaces[0].id);
          setTarget('/w/' + workspaces[0].id + '/' + next);
        } else {
          setTarget('/onboarding');
        }
      })
      .catch((err) => {
        if (!active) return;
        if (err instanceof ApiError && err.status === 401) {
          setTarget('/login');
        } else {
          // Onboarding is for a user who HAS no workspace — which only a successful, empty answer
          // can say. A failed request says nothing about it, and sending somebody who already has a
          // workspace off to create another was exactly the bug.
          setFailure(describeApiError(err, { fallback: "Couldn't load your workspaces." }));
        }
      });
    return () => {
      active = false;
    };
  }, [next, attempt]);

  if (failure) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-2 px-4 text-center">
        <p className="font-medium">{failure.title}</p>
        {failure.description && <p className="text-sm text-muted-foreground">{failure.description}</p>}
        <Button variant="outline" className="mt-2" onClick={() => setAttempt((n) => n + 1)}>Try again</Button>
      </div>
    );
  }

  if (!target) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return <Navigate to={target} replace />;
}
